import { useEffect, useRef, useState } from 'react';
import type { DiscussionTimer as TimerState, Lang } from '../../../shared/src/types.js';
import { usePerfLite } from '../usePerfLite.js';
import { serverClockOffset, timerDisplay } from '../timerMath.js';

interface Props {
  t: Lang;
  timer: TimerState;
}

// Live countdown driven by a 1s tick. The timer is purely cosmetic;
// when it hits zero the host still presses "start vote" by hand, so we just
// freeze the display at 0:00 and flag it as expired.
export function DiscussionTimerView({ t, timer }: Props) {
  const [now, setNow] = useState(() => Date.now());
  const lite = usePerfLite();
  useEffect(() => {
    const id = setInterval(() => setNow(Date.now()), 1000);
    return () => clearInterval(id);
  }, []);
  // Server-client clock offset (audit P2): the timer's timestamps are server
  // wall-clock, so rendering them against the raw device clock shifted the
  // countdown 1:1 with any skew. Each broadcast stamps a fresh `serverNow`;
  // sample the offset once per stamp (NOT every render — the stamp is fixed
  // while the local clock ticks, so re-deriving it would freeze the timer).
  const [offset, setOffset] = useState(() =>
    serverClockOffset(timer.serverNow, Date.now()),
  );
  useEffect(() => {
    setOffset(serverClockOffset(timer.serverNow, Date.now()));
  }, [timer.serverNow]);

  const { min, sec, expired, urgent, pct } = timerDisplay(timer, now, offset);

  // Fire a single haptic cue the moment the timer crosses 0. Keyed on
  // `expired` and latched with a ref so it triggers exactly once per expiry
  // (not on every 1s tick or re-render). Suppressed under perf-lite /
  // reduced-motion, since the user has opted out of motion cues.
  const cuedRef = useRef(false);
  useEffect(() => {
    if (!expired) {
      cuedRef.current = false;
      return;
    }
    if (cuedRef.current) return;
    cuedRef.current = true;
    if (!lite) {
      navigator.vibrate?.([120, 60, 120]);
    }
  }, [expired, lite]);

  return (
    <div className={`disc-timer ${expired ? 'expired' : ''} ${urgent ? 'urgent' : ''}`.trim()}>
      <div className="disc-timer-label sub" style={urgent ? { color: 'var(--poison-red)' } : undefined}>
        {expired
          ? t === 'ru' ? 'ВРЕМЯ ВЫШЛО' : 'ЧАС ВИЙШОВ'
          : urgent
            ? t === 'ru' ? 'ВРЕМЯ НА ИСХОДЕ' : 'ЧАС СПЛИВАЄ'
            : t === 'ru' ? 'ОБСУЖДЕНИЕ' : 'ОБГОВОРЕННЯ'}
      </div>
      <div
        className="disc-timer-clock mono"
        style={urgent ? { color: 'var(--poison-red)' } : undefined}
      >
        {String(min).padStart(2, '0')}:{String(sec).padStart(2, '0')}
      </div>
      <div className="disc-timer-bar">
        <div
          className="disc-timer-bar-fill"
          style={{
            width: `${pct * 100}%`,
            ...(urgent
              ? {
                  background: 'var(--poison-red)',
                  ...(lite ? {} : { animation: 'flicker 0.9s linear infinite' }),
                }
              : {}),
          }}
        />
      </div>
    </div>
  );
}
